import * as React from "react";
import { useEffect, useState } from "react";
import { Checkbox as BaseCheckbox, Spinner } from "react-ui";

import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import {
    UserFriendlyLoadingState,
    useUserFriendlyLoadingState,
} from "../../FormParts/UserPicklistDeclaration/SelectNidusEntryModal/SelectEntryModal/UserFriendlyLoadingState";

export interface SelectAllCheckboxProps extends ControlTooltipProps {
    value: boolean | undefined;
    onChangeAsync: (value: boolean) => Promise<void>;
    disabled?: boolean;
    isReadOnly?: boolean;
    "data-tid"?: string;
    isInProcessing?: boolean;
}

export const SelectAllCheckbox = (props: SelectAllCheckboxProps) => {
    const checkboxRef = React.createRef<BaseCheckbox>();
    const [isInProcessing, setIsInProcessing] = useState(false);
    const isBusy = isInProcessing || (props.isInProcessing ?? false);
    const busyState = useUserFriendlyLoadingState(isBusy);
    const isDisabled = props.disabled || props.isReadOnly || busyState >= UserFriendlyLoadingState.LoadingState2;
    const showSpinner = busyState >= UserFriendlyLoadingState.LoadingState3;

    const handleChange = async () => {
        if (!isBusy) {
            setIsInProcessing(true);
            try {
                await props.onChangeAsync(props.value == undefined ? true : !props.value);
            } finally {
                setIsInProcessing(false);
            }
        }
    };

    const handleRequestFocus = () => {
        checkboxRef.current?.focus();
    };

    useEffect(() => {
        if (props.value == undefined) {
            checkboxRef.current?.setIndeterminate();
        } else {
            checkboxRef.current?.resetIndeterminate();
        }
    }, [props.value]);

    useTestPageObject(
        () => ({
            getType: () => "Checkbox",
            isChecked: (): boolean => props.value != undefined && props.value,
            setChecked: handleChange,
        }),
        [props.value, handleChange]
    );

    return (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            {showSpinner ? (
                <div style={{ height: "32px", display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <Spinner type="mini" caption="" />
                </div>
            ) : (
                <BaseCheckbox
                    ref={checkboxRef}
                    checked={props.value ?? false}
                    disabled={isDisabled}
                    initialIndeterminate={props.value == undefined}
                    onChange={handleChange}
                    data-tid={props["data-tid"]}
                />
            )}
        </FocusManagementObserver>
    );
};
